import {FormWrapper} from "./items/form.wrapper";
import {AbstractControl, ValidationErrors, Validators} from "@angular/forms";
import {FormStateEnum} from "./items/form-state.enum";
import {isDefined, isNumeric, nonBlank} from "../utils/epic.utils";
import {Size} from "../../base/data/size";
import {LoggingService} from "../../services/logger/logging.service";
import {inject} from "@angular/core";
import {I18nService} from "../../services/i18n/i18n.service";
import {PermissionService} from "../../services/auth/permission.service";
import {UrlService} from "../../services/url.service";
import {FormService} from "../../services/form.service";
import {StateService} from "../../services/state/state.service";
import {FormInputConfig} from "./form-input/form-input-config";
import {FormTextEditorConfig} from "./form-text-editor/form-text-editor.config";
import {FormSelectConfig} from "./form-select/form-select.config";
import {FormDatepickerConfig} from "./form-datepicker/form-datepicker.config";
import {ErrorHelper} from "../../services/error/error.helper";
import {FormSelectOption} from "./items/form-select-option";

export abstract class AbstractFormFieldComponent {

  protected readonly log: LoggingService = inject(LoggingService);
  protected readonly i18n: I18nService = inject(I18nService);
  protected readonly permissionService: PermissionService = inject(PermissionService);
  protected readonly urlService: UrlService = inject(UrlService);
  protected readonly formService: FormService = inject(FormService);
  protected readonly stateService: StateService = inject(StateService);
  protected readonly errorHelper: ErrorHelper = inject(ErrorHelper);

  protected readonly smallInput: FormInputConfig = new FormInputConfig({size: Size.SMALL});
  protected readonly smallEditor: FormTextEditorConfig = new FormTextEditorConfig({size: Size.SMALL});
  protected readonly smallSelect: FormSelectConfig = new FormSelectConfig({size: Size.SMALL});
  protected readonly smallDatepicker: FormDatepickerConfig = new FormDatepickerConfig({size: Size.SMALL});

  // override this method
  protected isViewOnly(): boolean {
    return false;
  }

  // override this method
  protected getSize(): Size | undefined {
    throw new Error("Method not implemented.");
  }

  // override this method if needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,@typescript-eslint/no-empty-function,@typescript-eslint/explicit-module-boundary-types
  protected afterValueChanged(_name: string, _newValue: any, _oldValue: any, _initialValue: boolean): void {
  }

  protected getFormGroupClass(): string {
    const size: Size | undefined = this.getSize();
    const fgSize: string = isDefined(size) ? 'form-group-' + size.value : '';
    return ('form-group ' + fgSize).trim();
  }

  protected _onChange(name: string, wrapper: FormWrapper, event: Event, initialValue = false): boolean {
    const isSupportedTarget = event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement;
    if (isSupportedTarget && wrapper.isReady) {
      const newValue: string = event.target.value;
      const oldValue: any = wrapper.getValue(name);
      return this._onChangeValue(name, wrapper, newValue, oldValue, initialValue);
    }
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected _onChangeValue(name: string, wrapper: FormWrapper, newValue: any, oldValue?: any, initialValue: boolean = false): boolean {
    const useOldValue: any = oldValue ? oldValue : wrapper.getValue(name);
    this._onTouch(name, wrapper, initialValue);
    if (wrapper.isReady) {
      if (newValue !== useOldValue) {
        if (initialValue) {
          wrapper.patchValue(name, newValue);
        } else {
          wrapper.setValue(name, newValue);
        }
        this.afterValueChanged(name, newValue, useOldValue, initialValue);
        return true;
      }
    }
    return false;
  }

  protected _onTouch(name: string, wrapper: FormWrapper, initialValue: boolean = false): void {
    if (wrapper.isReady && !initialValue) {
      wrapper.form.controls[name].markAsTouched();
    }
  }

  protected _updateState(_name: string, wrapper: FormWrapper, state: FormStateEnum): void {
    if (wrapper.isReady) {
      wrapper.updateState(state);
    }
  }

  /**
   * A distinction between Angular Validator and Backend Validator would be desirable here.
   * So that backend errors are always displayed, even if the field is not touched.
   *
   * If Angular Validator: wrapper.form.controls[name].touched === true
   * If Backend validator: wrapper.form.controls[name].touched === doesn't matter
   */
  protected _isInvalid(name: string, wrapper: FormWrapper): boolean {
    if (wrapper.isReady) {
      const control: AbstractControl = wrapper.form.controls[name];
      if (isDefined(control)) {
        const invalid: boolean = wrapper.form.touched && control.invalid;
        return invalid && (control.touched || this._hasApiError(name, wrapper));
      } else {
        this.log.warn('FormComponent: form control "{}" not found', [name]);
      }
    }
    return false;
  }

  protected _hasApiError(name: string, wrapper: FormWrapper): boolean {
    const errors: ValidationErrors | null = wrapper.form.controls[name].errors;
    if (isDefined(errors)) {
      return Object.keys(errors).some((errKey) => {
        const apiError: string | undefined = Object.keys(errors[errKey]).find((k) => k.startsWith('api'));
        return nonBlank(apiError);
      });
    }
    return false;
  }

  protected _isRequiredField(name: string, wrapper: FormWrapper): boolean {
    if (wrapper.isViewOnly()) {
      return false;
    }
    if (wrapper.isReady && Object.prototype.hasOwnProperty.call(wrapper.form.controls, name)) {
      return wrapper.form.controls[name].hasValidator(Validators.required);
    }
    return false;
  }

  protected _isViewOnly(name: string, wrapper: FormWrapper): boolean {
    return wrapper.isViewOnly(name);
  }

  protected _isDisabled(name: string, wrapper: FormWrapper): boolean {
    return wrapper.getControl(name).disabled;
  }

  protected _hasSelectedOptions(name: string, wrapper: FormWrapper): boolean {
    if (wrapper.isReady && wrapper.hasControl(name)) {
      const formValue: any = wrapper.getValue(name);
      const selected: FormSelectOption | undefined = wrapper.getSelectOptions(name).find((o) => {
        if (o.value === formValue) {
          return true;
        } else if (isNumeric(o.value) && isNumeric(formValue)) {
          return Number(o.value) === Number(formValue);
        }
        return false;
      });
      return isDefined(selected);
    }
    return false;
  }
}
